/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import React from 'react';
import { formatDigits } from '../../Utils/formatNumbers';
import { dateStringTimeConvert } from '../../Utils/titleCaseFunction';
import { useCompany } from '../../Context/company-context';

const container = css`
  width: 100%;
  margin: auto;
  padding: 20px 30px;
  font-family: Arial, Helvetica, sans-serif;
  color: #333;
  background-color: #fff;
  box-sizing: border-box;

  @media print {
    @page { size: A4 portrait; margin: 12mm; }
    min-height: 297mm;
  }
`;

const header = css`
  text-align: center;
  margin-bottom: 25px;
  h1 {
    color: #d32f2f;
    font-size: 26pt;
    margin: 10px 0 5px;
    font-weight: bold;
  }
  h2 { font-size: 18pt; margin: 5px 0; }
`;

const customerInfo = css`
  width: 100%;
  margin-bottom: 25px;
  border-collapse: collapse;
  td { padding: 6px 8px; font-size: 14pt; vertical-align: top; }
`;

const tableStyle = css`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  th, td {
    border: 1px solid #555;
    padding: 8px 10px;
    font-size: 13.5pt;
  }
  th {
    background-color: #f4f4f4;
    font-weight: bold;
    text-align: left;
  }
  td.amount { text-align: right; }
`;

const totalsStyle = css`
  width: 100%;
  margin-top: 15px;
  font-size: 16pt;
  td.right { text-align: right; font-weight: bold; }
`;

const ProformaInvoiceContent = ({ customer, products }) => {
  const { state: { companyName, address, mobile, logo } } = useCompany();

  const invoiceDate = customer.date ? dateStringTimeConvert(customer.date) : '';
  const totalAmount = Number(customer.total_amount) || 0;

  return (
    <div css={container}>
      {/* Header */}
      <div css={header}>
        {logo && <img src={logo} alt={companyName} style={{ maxWidth: '160px', marginBottom: '10px' }} />}
        <h1>PROFORMA INVOICE</h1>
        <h2>{companyName || "PHARMACY"}</h2>
        <p>{address}</p>
        <p>Tel: {mobile}</p>
      </div>

      {/* Customer Information */}
      <table css={customerInfo}>
        <tbody>
          <tr>
            <td><strong>Proforma To:</strong> {customer.customer_name}</td>
            <td><strong>Proforma No:</strong> {customer.proforma_invoice_number || customer.invoice_number}</td>
          </tr>
          <tr>
            <td><strong>Contact:</strong> {customer.customer_mobile || 'N/A'}</td>
            <td><strong>Date:</strong> {invoiceDate}</td>
          </tr>
          <tr>
            <td><strong>Address:</strong> {customer.customer_address || 'N/A'}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      {/* Products Table - No Batch & Expiry */}
      <table css={tableStyle}>
        <thead>
          <tr>
            <th>No.</th>
            <th>Product Name</th>
            <th>Quantity</th>
            <th>Rate</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => {
            const qty = Number(p.quantity) || 0;
            const rate = Number(p.rate) || 0;
            const total = qty * rate;

            return (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{p.product_name}</td>
                <td>{qty}</td>
                <td className="amount">{formatDigits(rate)}</td>
                <td className="amount">{formatDigits(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals */}
      <table css={totalsStyle}>
        <tbody>
          <tr>
            <td><strong>Total Amount:</strong></td>
            <td className="right">{formatDigits(totalAmount)}</td>
          </tr>
        </tbody>
      </table>

      {/* Footer Note */}
      <div style={{ marginTop: '50px', textAlign: 'center', fontSize: '14pt', color: '#555' }}>
        <p><strong>This is a Proforma Invoice only.</strong></p>
        <p>It is not a valid tax invoice until converted.</p>
        <p>Thank you for your business!</p>
      </div>
    </div>
  );
};

export default ProformaInvoiceContent;